import React, { useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom';
import { Alert, Button, Tooltip } from '@mui/material';
import { Dayjs } from 'dayjs';

import { AppConfig } from '../../config/appConfig';

import { IMaterialInventory } from '../../types/material';

import { UtilityService } from '../../services/utilityService';

import { Decimal } from '../../decimal';
import { Icons } from '../icons';

export interface IMaterialInventoryTableProps {
	data?: IMaterialInventory[];
	id?: string;
	startDate?: Dayjs;
	endDate?: Dayjs;
	customer?: string;
	loadStatus?: string;
	container?: string;
	age?: string;
	onFilteredResultsUpdated?: (results: IMaterialInventory[]) => void;
}

const MaterialInventoryTable: React.FunctionComponent<IMaterialInventoryTableProps> = ({ data, id, startDate, endDate, customer, loadStatus, container, age, onFilteredResultsUpdated }) => {
	const [dataView, setDataView] = useState<IMaterialInventory[]>([]);
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(AppConfig.defaultPageSize);
	const [sortField, setSortField] = useState(AppConfig.materialInventoryListDefaultSortField);
	const [sortAscending, setSortAscending] = useState(true);
	const [errorMessage, setErrorMessage] = useState<string | undefined>();

	useEffect(() => {
		if (data) {
			let newData = data.filter((item) => {
				if (id && id.trim().length > 0 && !item.id?.toString().startsWith(id.trim())) {
					return false;
				}
				if (customer && customer != "All" && item.customerId?.toString() != customer) {
					return false;
				}
				if (loadStatus && loadStatus != "All" && ((loadStatus === "Loaded" && !item.remoteShipmentId) || (loadStatus === "Not Loaded" && item.remoteShipmentId))) {
					return false;
				}
				if (container && container != "All" && item.containerId?.toString() != container) {
					return false;
				}
				if (age && age != "custom") {
					const ageDays = parseInt(age);
					const creationDate = new Date(item.creationDate);
					const today = new Date();
					const diffTime = Math.abs(today.valueOf() - creationDate.valueOf());
					const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
					if ((ageDays >= 0 && diffDays < ageDays) || (ageDays < 0 && diffDays >= -ageDays)) {
						return false;
					}
				}
				return true;
			}).slice();

			// Start sort as descending if the field name starts with "-"
			let _sortField = sortField;
			if (sortField.startsWith("-")) {
				setSortAscending(false);
				_sortField = sortField.substring(1);
				setSortField(_sortField);
			}

			if (sortField) {
				switch (sortField) {
					case "id": {
						newData.sort((a, b) => UtilityService.compareNumbers(a.id, b.id));
						break;
					}
					case "material": {
						newData.sort((a, b) => UtilityService.compareStrings(a.materialName, b.materialName));
						break;
					}
					case "weight": {
						newData.sort((a, b) => UtilityService.compareNumbers(a.weight * a.uomLbsConversion, b.weight * b.uomLbsConversion));
						break;
					}
					case "container": {
						newData.sort((a, b) => UtilityService.compareStrings(a.containerName, b.containerName));
						break;
					}
					case "createdon": {
						newData.sort((a, b) => UtilityService.compareNumbers(new Date(a.creationDate).valueOf(), new Date(b.creationDate).valueOf()));
						break;
					}
					case "load": {
						newData.sort((a, b) => UtilityService.compareNumbers(a.remoteShipmentId ? 1 : 0, b.remoteShipmentId ? 1 : 0));
						break;
					}
					case "position": {
						// Rows are now formatted like 1A, 12B, etc. Need to split into level and position for proper sorting
						newData.sort((a, b) => {
							const rowParts1 = { level: a.remoteShipmentPosition.substring(a.remoteShipmentPosition.length - 1), position: parseInt(a.remoteShipmentPosition.substring(0, a.remoteShipmentPosition.length - 1)) };
							const rowParts2 = { level: b.remoteShipmentPosition.substring(b.remoteShipmentPosition.length - 1), position: parseInt(b.remoteShipmentPosition.substring(0, b.remoteShipmentPosition.length - 1)) };
							return UtilityService.compareTwoFields(rowParts1, rowParts2, 'level', 'position', "string", "number");
						});
						break;
					}
					case "location": {
						newData.sort((a, b) => UtilityService.compareStrings(a.customerLocationName, b.customerLocationName));
						break;
					}
					default: {
						newData.sort((a, b) => UtilityService.compareStrings(a.customerName, b.customerName));
						break;
					}
				}
			}
			if (!sortAscending) newData.reverse();

			setDataView(newData);
			onFilteredResultsUpdated?.(newData);
		}
	}, [data, id, customer, loadStatus, container, age, sortField, sortAscending]);

	useEffect(() => {
		if (id?.trim()) {
			setSortField("id");
			setSortAscending(true);
		}
	}, [id])

	useEffect(() => {
		setPage(0);
	}, [customer, container, loadStatus, age]);

	const handleHeaderClick = (field: string) => {
		if (sortField == field) {
			setSortAscending(!sortAscending);
		}
		else {
			setSortAscending(true);
			setSortField(field);
		}
	}

	const handlePageBack = () => {
		setPage(page - 1);
	}

	const handlePageForward = () => {
		setPage(page + 1);
	}

	return (
		<>
			{errorMessage &&
				<Alert className="alert-error" severity="error">{errorMessage}</Alert>
			}
			<div className="hwi-entity-search-results-table">
				<div className="headers">
					<div className="col-id">
						<p onClick={() => handleHeaderClick("id")}>
							ID
							{" "}
							{(sortField == "id" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
							{(sortField == "id" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
						</p>
					</div>
					<div className="col-material">
						<p onClick={() => handleHeaderClick("material")}>
							Material
							{" "}
							{(sortField == "material" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
							{(sortField == "material" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
						</p>
					</div>
					<div className="col-weight">
						<p onClick={() => handleHeaderClick("weight")}>
							Weight
							{" "}
							{(sortField == "weight" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
							{(sortField == "weight" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
						</p>
					</div>
					<div className="col-container">
						<p onClick={() => handleHeaderClick("container")}>
							Container
							{" "}
							{(sortField == "container" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
							{(sortField == "container" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
						</p>
					</div>
					<div className="col-date">
						<p onClick={() => handleHeaderClick("createdon")}>
							Added
							{" "}
							{(sortField == "createdon" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
							{(sortField == "createdon" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
						</p>
					</div>
					<div className="col-load">
						<p onClick={() => handleHeaderClick("load")}>
							Load
							{" "}
							{(sortField == "load" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
							{(sortField == "load" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
						</p>
					</div>
					<div className="col-position">
						<p onClick={() => handleHeaderClick("position")}>
							Position
							{" "}
							{(sortField == "position" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
							{(sortField == "position" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
						</p>
					</div>
					<div className="col-location">
						<p onClick={() => handleHeaderClick("location")}>
							Location
							{" "}
							{(sortField == "location" && sortAscending) && <Icons.SortAscAlpha height="16px"></Icons.SortAscAlpha>}
							{(sortField == "location" && !sortAscending) && <Icons.SortDescAlpha height="16px"></Icons.SortDescAlpha>}
						</p>
					</div>
					<div className="col-controls"></div>
				</div>
				<div className="data">
					{dataView?.length > 0 ?
						(
							dataView?.slice(page * pageSize, (page + 1) * pageSize).map((item, index) => (
								<div className="row" key={index}>
									<div className="col-id">
										<p>
											<span className="label">ID: </span>
											{item.id}
										</p>
									</div>
									<div className="col-material">
										<p>
											<span className="label">Material: </span>
											{item.materialName}
										</p>
									</div>
									<div className="col-weight">
										<p>
											<span className="label">Weight: </span>
											<Tooltip
												title={new Decimal(item.weight).mul(item.uomLbsConversion).toNumber().toLocaleString() + " pounds"}
												placement='right'
												arrow={true}
												disableHoverListener={item.uomName == "pounds"}
											>
												<span>
													{item.weight.toLocaleString()} {item.uomName}
												</span>
											</Tooltip>
										</p>
									</div>
									<div className="col-container">
										<p>
											<span className="label">Container: </span>
											<span className={item.containerName == "None" ? "inactive" : ""}>{item.containerName}{item.quantity > 1 ? ` x ${item.quantity}` : ""}</span>
										</p>
									</div>
									<div className="col-date">
										<p>
											<span className="label">Added: </span>
											{item.creationDate.toLocaleDateString()}
										</p>
									</div>
									<div className="col-load">
										<p>
											<span className="label">Load: </span>
											{item.remoteShipmentId &&
												item.remoteShipmentStatus == "DELETED" ? (
												<span className='deleted'>Deleted</span>
											) : (
												<NavLink to={"/tils/" + item.remoteShipmentId}>
													<Icons.ViewItem fill="#272f50" height="14px"></Icons.ViewItem>
													{" "}
													View
												</NavLink>
											)}
										</p>
									</div>
									<div className="col-position">
										<p>
											<span className="label">Position: </span>
											{item.remoteShipmentPosition}
										</p>
									</div>
									<div className="col-location">
										<p>
											<span className="label">Location: </span>
											<span className={item.customerLocationId ? "" : "inactive"}>{item.customerLocationName}</span>
										</p>
									</div>
									<div className="col-controls">
										<p>
											<NavLink to={"/materialinventory/" + item.id}>
												<Icons.ViewItem fill="#272f50" height="14px"></Icons.ViewItem>
												{" "}
												View
											</NavLink>
										</p>
									</div>
								</div>
							))
						) :
						(
							<div className="no-results">0 results{age == "custom" && <>.<br />Try expanding the date range to include more results.</>}</div>
						)
					}
				</div>
				{dataView?.length > 0 &&
					<div className="pager">
						<div className="pager-button"><Button variant="outlined" fullWidth={true} disabled={page < 1} onClick={handlePageBack}>Previous</Button></div>
						<div className="pager-text">{`${(page * pageSize) + 1} to ${((page + 1) * pageSize) <= dataView.length ? ((page + 1) * pageSize) : dataView.length} of ${dataView.length}`}</div>
						<div className="pager-button"><Button variant="outlined" fullWidth={true} disabled={((page + 1) * pageSize) >= dataView.length} onClick={handlePageForward}>Next</Button></div>
					</div>
				}
			</div>
		</>
	)
}

export default MaterialInventoryTable;